/**
 * Chat API — REST endpoints for Onboardix Messenger.
 * WebSocket handled separately via useChat hook.
 */
import { apiFetch } from './core'

export const chatApi = {
    /* ── Rooms ───────────────────────────────────── */
    listRooms: () => apiFetch('/chat/rooms'),

    getRoom: (roomId) => apiFetch(`/chat/rooms/${roomId}`),

    createDirectRoom: (otherUserId) =>
        apiFetch('/chat/rooms/direct', {
            method: 'POST',
            body: JSON.stringify({ other_user_id: otherUserId }),
        }),

    createGroupRoom: (payload) =>
        apiFetch('/chat/rooms/group', {
            method: 'POST',
            body: JSON.stringify(payload),
        }),

    updateRoom: (roomId, payload) =>
        apiFetch(`/chat/rooms/${roomId}`, {
            method: 'PATCH',
            body: JSON.stringify(payload),
        }),

    leaveRoom: (roomId) =>
        apiFetch(`/chat/rooms/${roomId}`, { method: 'DELETE' }),

    pinRoomMessage: (roomId, messageId) =>
        apiFetch(`/chat/rooms/${roomId}/pin`, {
            method: 'POST',
            body: JSON.stringify({ message_id: messageId }),
        }),

    /* ── Messages ────────────────────────────────── */
    listMessages: (roomId, { limit = 50, before } = {}) => {
        const params = new URLSearchParams({ limit: String(limit) })
        if (before) params.set('before', before)
        return apiFetch(`/chat/rooms/${roomId}/messages?${params}`)
    },

    sendMessage: (roomId, payload) =>
        apiFetch(`/chat/rooms/${roomId}/messages`, {
            method: 'POST',
            body: JSON.stringify(payload),
        }),

    deleteMessage: (messageId) =>
        apiFetch(`/chat/messages/${messageId}`, { method: 'DELETE' }),

    /* ── Reactions ────────────────────────────────── */
    toggleReaction: (messageId, emoji) =>
        apiFetch(`/chat/messages/${messageId}/reactions`, {
            method: 'POST',
            body: JSON.stringify({ emoji }),
        }),

    /* ── Read status ─────────────────────────────── */
    markRead: (roomId) =>
        apiFetch(`/chat/rooms/${roomId}/read`, { method: 'POST' }),

    markMessageRead: (messageId) =>
        apiFetch(`/chat/messages/${messageId}/read`, { method: 'POST' }),

    /* ── Participants ────────────────────────────── */
    addParticipant: (roomId, userId, role = 'member') =>
        apiFetch(`/chat/rooms/${roomId}/participants`, {
            method: 'POST',
            body: JSON.stringify({ user_id: userId, role }),
        }),

    removeParticipant: (roomId, userId) =>
        apiFetch(`/chat/rooms/${roomId}/participants/${userId}`, {
            method: 'DELETE',
        }),

    /* ── Announcements ───────────────────────────── */
    confirmAnnouncement: (messageId) =>
        apiFetch(`/chat/announcements/${messageId}/confirm`, { method: 'POST' }),

    announcementStats: (messageId) =>
        apiFetch(`/chat/announcements/${messageId}/stats`),

    /* ── Online status ───────────────────────────── */
    getOnlineUsers: () => apiFetch('/chat/online'),

    /* ── Employee updates ────────────────────────── */
    createUpdate: (payload) =>
        apiFetch('/chat/updates', {
            method: 'POST',
            body: JSON.stringify(payload),
        }),

    getTeamUpdates: (managerEmployeeId) =>
        apiFetch(`/chat/updates/team/${managerEmployeeId}`),

    /* ── Peer feedback ───────────────────────────── */
    createFeedback: (payload) =>
        apiFetch('/chat/feedback', {
            method: 'POST',
            body: JSON.stringify(payload),
        }),

    listFeedback: (direction = 'received') =>
        apiFetch(`/chat/feedback?direction=${direction}`),

    acknowledgeFeedback: (feedbackId) =>
        apiFetch(`/chat/feedback/${feedbackId}/ack`, { method: 'POST' }),

    searchUsers: (q = '') =>
        apiFetch(`/chat/users/search?q=${encodeURIComponent(q)}`),
}
